/**
 * `applyOutcomeSuggestionVoteOptimistic` — optimistic-cache helper for
 * the `useVoteOnOutcomeSuggestion` mutation.
 *
 * Patches `viewerKind` on the suggestion row matching `vars.id` so the
 * up/down arrow flips the moment the user clicks, before the network
 * round-trip. Counts (`upvotes`, `downvotes`, `score`) are NOT touched
 * here — they're authoritative aggregates from the backend and arrive
 * within ~100 ms over NATS via `applyOutcomeSuggestionVote`.
 *
 * Cache shape: `useGetOutcomeSuggestions` is `kind: multiple`, so `prev`
 * is `PagedData<Row>`: `{ data: Row[], nextCursor, hasMore, _sort? }`.
 * Sibling `applyProposalVoteOptimistic` documents the same shape lesson.
 *
 * `vars.kind` discriminates the action:
 *   • `'up'` / `'down'` → set viewerKind to that kind.
 *   • `null`            → clear viewerKind (the backend route accepts
 *     `kind: null` to clear an existing vote; OpenAPI typed the enum
 *     without `null`, so the call site passes a runtime `null` via an
 *     `as unknown as` cast — that runtime value reaches us here).
 *
 * Immutable: returns the exact `prev` reference when no row matched so
 * structural-sharing short-circuits stay intact.
 *
 * Platform-agnostic — pure data, no web/RN API. Safe in core.
 */

type ViewerKindValue = 'up' | 'down' | null;

interface SuggestionRowLike {
  readonly id?: string;
  readonly viewerKind?: ViewerKindValue;
}

interface PagedDataLike<Row> {
  readonly data?: ReadonlyArray<Row>;
}

function nextViewerKindFrom(vars: Record<string, unknown>): ViewerKindValue {
  const kind = vars.kind;
  if (kind === 'up' || kind === 'down') return kind;
  return null;
}

function patchRowsById<Row extends SuggestionRowLike>(
  rows: ReadonlyArray<Row>,
  id: string,
  nextKind: ViewerKindValue,
): { rows: ReadonlyArray<Row>; changed: boolean } {
  let changed = false;
  const next = rows.map((row) => {
    if (row === null || row === undefined || row.id !== id) return row;
    if (row.viewerKind === nextKind) return row;
    changed = true;
    return { ...row, viewerKind: nextKind };
  });
  return { rows: next, changed };
}

export function applyOutcomeSuggestionVoteOptimistic<T>(prev: T, vars: Record<string, unknown>): T {
  if (prev === null || prev === undefined || typeof prev !== 'object') return prev;
  const id = vars.id;
  if (typeof id !== 'string' || id.length === 0) return prev;
  const nextKind = nextViewerKindFrom(vars);

  if ('data' in prev && Array.isArray((prev as PagedDataLike<SuggestionRowLike>).data)) {
    const envelope = prev as PagedDataLike<SuggestionRowLike>;
    const rows = envelope.data as ReadonlyArray<SuggestionRowLike>;
    const { rows: nextRows, changed } = patchRowsById(rows, id, nextKind);
    if (!changed) return prev;
    return { ...(prev as object), data: nextRows } as T;
  }

  return prev;
}
